/**
 * User Cache Service - Centralized caching for user list data
 * 
 * This service provides a single source of truth for user data across the application,
 * preventing redundant API calls in components like addform and editform.
 * 
 * Features:
 * - Fetches all users with pagination support
 * - Caches user list and provides key-value mapping
 * - Manual refresh capability
 * - Observable-based API for reactive components
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { shareReplay, tap, switchMap } from 'rxjs/operators';
import { IdentityUserDto, IdentityUserService } from '@abp/ng.identity/proxy';
import { PagedResultDto } from '@abp/ng.core';

export interface UserKeyValue {
  key: string;  // Display name
  value: string;  // User ID
}

@Injectable({ providedIn: 'root' })
export class UserCacheService {
  private readonly reload$ = new BehaviorSubject<void>(undefined);
  private isLoading = false;
  private isLoaded = false;
  private loadPromise: Promise<void> | null = null;
  
  // Cached data
  private cachedUsers: IdentityUserDto[] = [];
  private cachedKeyValueList: UserKeyValue[] = [];
  private cachedPagedResult: PagedResultDto<IdentityUserDto> = { items: [], totalCount: 0 };

  constructor(private readonly userService: IdentityUserService) {}

  /**
   * Get observable that emits users with caching
   */
  getUsers$(): Observable<IdentityUserDto[]> {
    if (!this.isLoaded && !this.isLoading) {
      this.loadUsers();
    }
    
    return new Observable<IdentityUserDto[]>(subscriber => {
      if (this.isLoaded) {
        subscriber.next(this.cachedUsers);
        subscriber.complete();
      } else {
        // Wait for loading to complete
        const checkLoaded = setInterval(() => {
          if (this.isLoaded) {
            clearInterval(checkLoaded);
            subscriber.next(this.cachedUsers);
            subscriber.complete();
          }
        }, 50);
        
        // Timeout after 30 seconds
        setTimeout(() => {
          clearInterval(checkLoaded);
          if (!this.isLoaded) {
            subscriber.next(this.cachedUsers);
            subscriber.complete();
          }
        }, 30000);
      }
    });
  }

  /**
   * Get users synchronously (returns empty if not loaded)
   */
  getCachedUsers(): IdentityUserDto[] {
    return this.cachedUsers;
  }

  /**
   * Get user key-value mapping for dropdowns
   */
  getKeyValueMapping(): UserKeyValue[] {
    return this.cachedKeyValueList;
  }

  /**
   * Get paged result format for components expecting PagedResultDto
   */
  getPagedResult(): PagedResultDto<IdentityUserDto> {
    return this.cachedPagedResult;
  }

  /**
   * Check if cache is ready
   */
  isReady(): boolean {
    return this.isLoaded;
  }

  /**
   * Force refresh the cache
   */
  async refresh(): Promise<void> {
    this.isLoaded = false;
    await this.loadUsers();
  }

  /**
   * Get a single user by ID from cache
   */
  getUserById(userId: string): IdentityUserDto | undefined {
    return this.cachedUsers.find(u => u.id === userId);
  }

  /**
   * Get user display name by ID
   */
  getUserDisplayName(userId: string): string {
    const user = this.getUserById(userId);
    if (!user) return '';
    return this.formatUserDisplayName(user);
  }

  /**
   * Format user display name
   */
  formatUserDisplayName(user: IdentityUserDto): string {
    if (!user) return '';
    if (user.name === null) {
      return user.userName || '';
    }
    if (user.surname === null) {
      return user.name || '';
    }
    return `${user.name} ${user.surname}`.trim();
  }

  /**
   * Load all users with pagination
   */
  private async loadUsers(): Promise<void> {
    if (this.isLoading && this.loadPromise) {
      return this.loadPromise;
    }

    this.isLoading = true;
    this.loadPromise = (async () => {
      try {
        const users = await this.fetchAllUsers();
        this.cachedUsers = users;
        this.cachedKeyValueList = this.mapToKeyValue(users);
        this.cachedPagedResult = { items: users, totalCount: users.length };
        this.isLoaded = true;
      } catch (error) {
        console.error('Error loading user list:', error);
        this.cachedUsers = [];
        this.cachedKeyValueList = [];
        this.cachedPagedResult = { items: [], totalCount: 0 };
      } finally {
        this.isLoading = false;
      }
    })();

    try {
      await this.loadPromise;
    } finally {
      this.loadPromise = null;
    }
  }

  /**
   * Fetch all users with pagination to handle large user lists
   */
  private async fetchAllUsers(): Promise<IdentityUserDto[]> {
    const result: IdentityUserDto[] = [];
    const seen = new Set<string>();
    const pageSize = 500;
    let skipCount = 0;
    let expectedTotal = Number.MAX_SAFE_INTEGER;

    while (skipCount < expectedTotal) {
      const response = await firstValueFrom(
        this.userService.getList({ maxResultCount: pageSize, skipCount })
      );
      
      const items = response.items ?? [];
      for (const item of items) {
        if (!item?.id || seen.has(item.id)) {
          continue;
        }
        seen.add(item.id);
        result.push(item);
      }

      if (response.totalCount !== undefined && response.totalCount !== null) {
        expectedTotal = response.totalCount;
      } else if (items.length < pageSize) {
        expectedTotal = result.length;
      }

      if (!items.length) {
        break;
      }

      skipCount += items.length;
    }

    return result;
  }

  /**
   * Map user list to key-value format for dropdowns
   */
  private mapToKeyValue(users: IdentityUserDto[]): UserKeyValue[] {
    return users.map(user => ({
      key: this.formatUserDisplayName(user),
      value: user.id
    }));
  }
}
